import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { isFeatureEnabled, Feature } from '../../../config/FeaturesConfig';
import { CUSTOMER_COMMENTS_MAX_WIDTH } from '../../../Util/GlobalConstants';
import ContactListeningModal from '../../ContactListening/ContactListeningModal';
import ContactListening from '../../ContactListening/ContactListening';
import {
  ContactData,
  defaultContactData,
} from '../../../api/ContactListening/schema';
import DataState from '../../../Types/DataState';
import { QueryVariablesContactListeningData } from '../../../api/Naws';
import ContextMenu from '../../../commons/ContextMenu';
import HideCustomerCommentModal from './HideCustomerCommentModal';
import { StateContext } from '../../../GlobalStateStore';
import {
  HideCustomerCommentResponse,
  EMPTY_HIDE_CUSTOMER_COMMENT_RESPONSE,
} from '../../../Types/HideCustomerCommentResponse';
import DataSource from '../../../api';

const apiNaws = DataSource.getDataFeed('NAWS');

interface CustomerCommentsProps {
  customerMessages: Record<string, any>[];
  page: string;
  contactStartDate: string;
  contactEndDate: string;
  contactHandlerId?: string;
  hideCustomerCommentEnabledForTesting?: boolean;
}

const CustomerComments: React.FC<CustomerCommentsProps> = ({
  customerMessages,
  page,
  contactStartDate,
  contactEndDate,
  contactHandlerId,
  hideCustomerCommentEnabledForTesting,
}: CustomerCommentsProps): React.ReactElement => {
  const contactLinksEnabled = isFeatureEnabled(Feature.CONTACT_LINKS);
  const hideCustomerCommentEnabled =
    hideCustomerCommentEnabledForTesting || // for unit tests
    isFeatureEnabled(Feature.HIDE_CUSTOMER_COMMENT);
  const contactListeningEnabled = isFeatureEnabled(Feature.CONTACT_LISTENING);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingContactId, setRecordingContactId] = useState('');
  const [contactListeningData, setContactListeningData] =
    useState<ContactData>(defaultContactData);
  const [contactListeningDataState, setContactListeningDataState] =
    useState<string>(DataState.INITIALIZED);
  const [contactListeningParams, setContactListeningParams] =
    useState<QueryVariablesContactListeningData>();
  const [showCustomerComments, setShowCustomerComments] = useState(
    customerMessages.map(() => true)
  );
  const [hideCustomerComment, setHideCustomerComment] =
    useState<HideCustomerCommentResponse>(EMPTY_HIDE_CUSTOMER_COMMENT_RESPONSE);
  const [hideCustomerCommentState, setHideCustomerCommentState] = useState(
    DataState.INITIALIZED
  );
  const [hideCustomerCommentIndex, setHideCustomerCommentIndex] = useState(-1);
  const [hideCustomerCommentContactID, setHideCustomerCommentContactID] =
    useState('');
  const [
    hideCustomerCommentResponseDateTime,
    setHideCustomerCommentResponseDateTime,
  ] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const routeParams = useParams();
  const { isTm, cscEntityPrefix, userID } = inspireOptions.data;
  const isViewingOwnProfile = routeParams.userId
    ? routeParams.userId === userID
    : true;

  const showHideCommentButton =
    hideCustomerCommentEnabled && isViewingOwnProfile && !isTm;

  const isHideCustomerCommentSuccessful = () =>
    hideCustomerCommentState === DataState.LOADED &&
    hideCustomerComment.success;

  const isHideCustomerCommentUnSuccessful = () =>
    hideCustomerCommentState === DataState.ERROR ||
    (hideCustomerCommentState === DataState.LOADED &&
      !hideCustomerComment.success);

  const removeCustomerCommentRowToBeHidden = () => {
    const newShowCustomerCommentsState = [...showCustomerComments];
    newShowCustomerCommentsState[hideCustomerCommentIndex] = false;
    setShowCustomerComments(newShowCustomerCommentsState);
    setHideCustomerCommentIndex(-1);
    setHideCustomerCommentContactID('');
    setHideCustomerCommentResponseDateTime('');
  };

  useEffect(() => {
    if (isHideCustomerCommentSuccessful()) {
      setShowSuccessToast(true);
      removeCustomerCommentRowToBeHidden();
    } else if (isHideCustomerCommentUnSuccessful()) {
      setShowErrorToast(true);
    }
  }, [hideCustomerCommentState, hideCustomerComment]);

  const onCloseSuccessToast = useCallback(() => {
    setShowSuccessToast(false);
  }, []);

  const onCloseErrorToast = useCallback(() => {
    setShowErrorToast(false);
  }, []);

  const CLClickstream = {
    slotID: 'open-contact-listening-modal',
    tag1: page,
    tag2: 'Customer Comments',
  };

  const onHideCustomerComment = (
    customerCommentIndex: number,
    contactID: string,
    responseDateTime: string
  ) => {
    setHideCustomerCommentIndex(customerCommentIndex);
    setHideCustomerCommentContactID(contactID);
    setHideCustomerCommentResponseDateTime(responseDateTime);

    if (cscEntityPrefix && userID && contactID) {
      const hideCustomerCommentInput =
        apiNaws.prepareQueryForHideCustomerComment(
          `${cscEntityPrefix}:${userID}`,
          contactID,
          responseDateTime
        );

      apiNaws.hideCustomerComment(
        hideCustomerCommentInput,
        setHideCustomerComment,
        setHideCustomerCommentState
      );
    }
  };
  const onRetryHideCustomerComment = (
    customerCommentIndex: number,
    contactID: string,
    responseDateTime: string
  ) => {
    onCloseErrorToast();
    onHideCustomerComment(customerCommentIndex, contactID, responseDateTime);
  };
  const filteredCustomerMessages = customerMessages.filter(
    (_, customerMessageIndex) => showCustomerComments[customerMessageIndex]
  );

  return (
    <>
      {showSuccessToast && (
        <>
          <Toaster
            toasts={[
              {
                id: 'cs_perfportal_hide_customer_comment_success_toast',
                timeout: 3000,
              },
            ]}
            onCloseToast={onCloseSuccessToast}
            alignmentHorizontal='center'
          >
            {(): React.ReactElement => (
              <>
                <Alert
                  type='success'
                  toast={true}
                  onClose={onCloseSuccessToast}
                  data-csa-c-type='button'
                >
                  <Text alignment='center'>
                    <FormattedMessage
                      id='cs_perfportal_hide_customer_comment_success_message'
                      defaultMessage='Customer comment hidden successfully'
                    />
                  </Text>
                </Alert>
              </>
            )}
          </Toaster>
        </>
      )}

      {showErrorToast && (
        <>
          <Toaster
            toasts={[
              {
                id: 'cs_perfportal_hide_customer_comment_error_toast',
                timeout: 30000,
              },
            ]}
            onCloseToast={onCloseErrorToast}
            alignmentHorizontal='center'
          >
            {(): React.ReactElement => (
              <>
                <Alert type='error' toast={true} onClose={onCloseErrorToast}>
                  <Text alignment='center'>
                    <FormattedMessage
                      id='cs_perfportal_hide_customer_comment_error_message'
                      defaultMessage='Unable to hide customer comment.'
                    />
                    {<>&nbsp;</>}
                    <Link
                      data-testid='cs_perfportal_hide_customer_comment_error_toast_retry_link'
                      onClick={() => {
                        onRetryHideCustomerComment(
                          hideCustomerCommentIndex,
                          hideCustomerCommentContactID,
                          hideCustomerCommentResponseDateTime
                        );
                      }}
                      data-csa-c-type='button'
                      data-csa-c-slot-id='dashboard-table-contact-customer-comments-hide-customer-comments-error-toast-retry-button-slot'
                      data-csa-c-content-id={`dashboard-table-contact-customer-comments-hide-customer-comments-error-toast-retry-button-${hideCustomerCommentContactID}`}
                      data-csa-c-tag1={page}
                      data-csa-c-tag2='Customer Comments'
                      data-csa-c-tag3='Customer Comments Context Menu'
                      data-csa-c-tag4='Hide Customer Comment Modal'
                      data-csa-c-contactid={hideCustomerCommentContactID}
                      data-csa-c-is-tm={isTm}
                    >
                      <FormattedMessage
                        id='cs_perfportal_hide_customer_comment_error_message_retry'
                        defaultMessage='Retry'
                      />
                    </Link>
                  </Text>
                </Alert>
              </>
            )}
          </Toaster>
        </>
      )}
      {filteredCustomerMessages.length === 0 ? (
        <Text>
          <FormattedMessage
            id='cs_perfportal_noComments_coach_self'
            defaultMessage='You received no comments from customers this week. Try another week.'
          />
        </Text>
      ) : (
        <>
          <ContactListeningModal
            loadingState={contactListeningDataState}
            showModal={showRecordingModal}
            setShowModal={setShowRecordingModal}
            contactId={recordingContactId}
            contactListeningData={contactListeningData}
            contactListeningParams={contactListeningParams}
            CLClickstream={{
              ...CLClickstream,
              tag3: 'Contact Listening Modal',
            }}
          />
          <Table headerRows={1} showStripes showDividers>
            <TableRow highlightOnHover={false}>
              <TableCell width='25.29%'>
                <FormattedMessage
                  id='PerfPortal_contactId'
                  defaultMessage='Contact ID'
                />
              </TableCell>
              <TableCell>
                <FormattedMessage
                  id='cs_perfportal_comment'
                  defaultMessage='Comment'
                  description='Subcolumn for comment'
                />
              </TableCell>
              {showHideCommentButton && (
                <TableCell width='25.29%'>
                  <FormattedMessage
                    id='cs_perfportal_actions'
                    defaultMessage='Actions'
                  />
                </TableCell>
              )}
            </TableRow>
            {filteredCustomerMessages.map(
              (customerMessage, customerMessageIndex) => {
                const {
                  content: customerCommentText,
                  contactID,
                  responseDateTime,
                  marketplaceDomainName,
                } = customerMessage;
                // eslint-disable-next-line max-len
                const contactUrl = `/gp/stores/${marketplaceDomainName}/gp/communications/issue-manager?initialCommId=${contactID}`;
                return (
                  <TableRow key={contactUrl} highlightOnHover={false}>
                    <TableCell
                      width='12%'
                      data-cy={contactID}
                      data-cy-type='customer-comment-contact-id'
                      data-cy-contact-id={contactID}
                    >
                      {(contactLinksEnabled &&
                        marketplaceDomainName !== null) ||
                      contactListeningEnabled ? (
                        <ContactListening
                          contactID={contactID}
                          setContactListening={setContactListeningData}
                          setContactListeningState={
                            setContactListeningDataState
                          }
                          setContactListeningParams={setContactListeningParams}
                          setRecordingContactId={setRecordingContactId}
                          setShowRecordingModal={setShowRecordingModal}
                          clickstreamTags={{
                            'data-csa-c-slot-id':
                              'dashboard-table-contact-customer-comments-contact-id-slot',
                            'data-csa-c-content-id': `dashboard-table-customer-comments-contact-link-for-${contactID}`,
                            'data-csa-c-tag1': page,
                            'data-csa-c-tag2': 'Customer Comments',
                            'data-csa-c-contactid': contactID,
                          }}
                          cscEndpoint={contactUrl}
                          contactStartDate={contactStartDate}
                          contactEndDate={contactEndDate}
                          contactHandlerId={contactHandlerId}
                          CLClickstream={CLClickstream}
                        />
                      ) : (
                        contactID
                      )}
                    </TableCell>
                    <TableCell width='76%'>
                      <Box maxWidth={CUSTOMER_COMMENTS_MAX_WIDTH}>
                        <Text breakWord={true}>{customerCommentText}</Text>
                      </Box>
                    </TableCell>
                    {showHideCommentButton && (
                      <TableCell width='25%'>
                        <ContextMenu
                          id={`customer-comment-context-menu-contact-id-${contactID}`}
                          position='bottom'
                          clickstreamTags={{
                            'data-csa-c-type': 'button',
                            'data-csa-c-slot-id':
                              'dashboard-table-contact-customer-comments-context-menu-button-slot',
                            'data-csa-c-content-id': `dashboard-table-customer-comments-context-menu-button-for-${contactID}`,
                            'data-csa-c-tag1': page,
                            'data-csa-c-tag2': 'Customer Comments',
                            'data-csa-c-tag3': 'Customer Comments Context Menu',
                            'data-csa-c-contactid': contactID,
                            'data-csa-c-is-tm': isTm,
                          }}
                          menuItems={[
                            {
                              id: `hide-button-${contactID}`,
                              label: (
                                <FormattedMessage
                                  id='cs_perfportal_hide_customer_comment_button'
                                  defaultMessage='Hide'
                                />
                              ),
                              modal: (
                                <HideCustomerCommentModal
                                  contactId={contactID}
                                  clickstreamTags={{
                                    'data-csa-c-tag1': page,
                                    'data-csa-c-tag2': 'Customer Comments',
                                    'data-csa-c-tag3':
                                      'Customer Comments Context Menu',
                                    'data-csa-c-tag4':
                                      'Hide Customer Comment Modal',
                                    'data-csa-c-contactid': contactID,
                                    'data-csa-c-is-tm': isTm,
                                  }}
                                />
                              ),
                              onModalConfirm: () => {
                                onHideCustomerComment(
                                  customerMessageIndex,
                                  contactID,
                                  responseDateTime
                                );
                              },
                              type: 'primary',
                              clickstreamTags: {
                                'data-csa-c-type': 'button',
                                'data-csa-c-slot-id':
                                  'dashboard-table-contact-customer-comments-context-menu-hide-comment-button-slot',
                                'data-csa-c-content-id': `dashboard-table-contact-customer-comments-context-menu-hide-comment-button-for-${contactID}`,
                                'data-csa-c-tag1': page,
                                'data-csa-c-tag2': 'Customer Comments',
                                'data-csa-c-tag3':
                                  'Customer Comments Context Menu',
                                'data-csa-c-contactid': contactID,
                                'data-csa-c-is-tm': isTm,
                              },
                            },
                          ]}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              }
            )}
          </Table>
        </>
      )}
    </>
  );
};

export default CustomerComments;
